import type { WizardBand, WizardLane } from '../../services/schedulerWizardApi';
import type { WizardPrerequisites } from '../../services/schedulerWizardPrerequisites';
import { WIZARD_DAYS } from './schedulerWizardPresets';

export function wizardDutyLabel(lane: WizardLane, index: number): string {
  if (lane.role !== 'Primary') return `${lane.role} ${index + 1}`;
  const weekend = lane.bands.some(band => band.endDayOffset >= 3 && band.endDayOffset < 7);
  if (weekend) return lane.bands.length > 1 ? 'Night and whole weekend' : 'Weekend';
  if (lane.bands.some(band => band.endDayOffset === 7)) return 'Continuous on-call';
  if (lane.bands.every(band => band.startTime === '08:00' && band.endTime === '18:00')) return 'Daytime';
  if (lane.bands.every(band => band.startTime === '18:00' && band.endTime === '08:00')) return 'Overnight';
  if (lane.bands.length === 7 && lane.bands.every(band => band.startTime === lane.bands[0].startTime &&
    band.endTime === lane.bands[0].endTime)) return `Regional duty ${index + 1} (${lane.bands[0].startTime}-${lane.bands[0].endTime})`;
  return `Primary duty ${index + 1}`;
}

const minutes = (time: string) => {
  const [hours, mins] = time.split(':').map(Number);
  return hours * 60 + mins;
};
export function weeklyBandSegments(band: WizardBand): { start: number; end: number }[] {
  const day = WIZARD_DAYS.indexOf(band.startDay);
  const start = day * 1440 + minutes(band.startTime);
  const end = day * 1440 + band.endDayOffset * 1440 + minutes(band.endTime);
  if (day < 0 || !Number.isFinite(start) || !Number.isFinite(end) || end <= start) return [];
  const week = 10080;
  if (end - start >= week) return [{ start: 0, end: week }];
  return end > week ? [{ start, end: week }, { start: 0, end: end - week }] : [{ start, end }];
}
export function wizardBandLabel(band: WizardBand) {
  return `${band.startDay} ${band.startTime} - ${band.endDay} ${band.endTime}${band.endDayOffset ? ` (+${band.endDayOffset} day${band.endDayOffset === 1 ? '' : 's'})` : ''}`;
}

export function SchedulerPatternPreview({ lanes, timezone, snapshot, illustrateEscalation = false }: {
  lanes: WizardLane[]; timezone: string; snapshot?: WizardPrerequisites | null; illustrateEscalation?: boolean;
}) {
  const primary = lanes.filter(lane => lane.role === 'Primary');
  const rows = lanes.map((lane, index) => ({ id: lane.id, label: wizardDutyLabel(lane, index),
    bands: lane.bands, groupId: lane.groupId, role: lane.role, illustrative: false }));
  if (illustrateEscalation && !lanes.some(lane => lane.role === 'Escalation')) rows.push({
    id: 'escalation-illustration', label: 'Matched escalation (illustration only)',
    bands: primary.flatMap(lane => lane.bands), groupId: '', role: 'Escalation', illustrative: true,
  });
  return <section className="wizard-pattern-preview" aria-label="Weekly pattern layout">
    <h3>Weekly duty layout</h3>
    <p>Monday 00:00 to next Monday 00:00 in {timezone || 'the controlling timezone'}.
      Bars show duty periods, not assigned people or verified coverage. Ownership changes at the configured local handover, not at the chart's midnight.</p>
    <div className="wizard-pattern-scroll">
      <div className="wizard-pattern-axis"><span>Duty / allocation</span><div>{WIZARD_DAYS.map(day => <span key={day}>{day.slice(0, 3)}</span>)}</div></div>
      {rows.map((row, index) => <div className="wizard-pattern-row" key={row.id}>
        <div><strong>{row.label}</strong><br /><small>{row.illustrative ? 'No escalation roster configured' :
          snapshot?.groups.find(group => group.groupId === row.groupId)?.name || 'Group/user allocated in step 4'}</small></div>
        <div className={`wizard-pattern-track wizard-pattern-color-${row.role === 'Escalation' ? 'escalation' : index % 3}`}>
          {row.bands.flatMap(band => weeklyBandSegments(band).map((segment, part) => <span key={`${band.id}-${part}`}
            className={row.illustrative ? 'wizard-pattern-bar wizard-pattern-illustration' : 'wizard-pattern-bar'}
            style={{ left: `${segment.start / 10080 * 100}%`, width: `${(segment.end - segment.start) / 10080 * 100}%` }}
            title={`${row.label}: ${wizardBandLabel(band)}`} />))}
        </div>
      </div>)}
    </div>
    <details><summary>Read exact duty times</summary>
      {rows.map(row => <div key={row.id}><h4>{row.label}</h4><ul>{row.bands.map(band =>
        <li key={band.id}>{wizardBandLabel(band)}</li>)}</ul></div>)}
    </details>
  </section>;
}
